import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type OptionType } from '@/api/common';
import {
  type VendorSpendCellData,
  type VendorSpendDataType,
  endSession,
  mutateVendorSpend,
  startOrContinueSession,
  useVendorSpend,
} from '@/api/vendorSpend';
import PlusIcon from '@/assets/svg/bx-plus.svg?react';
import EditAddIcon from '@/assets/svg/edit-add-box.svg?react';
import { Button } from '@/components/buttons';
import PageError from '@/components/errors/PageError';
import { PageSpinner } from '@/components/loaders';
import { Page } from '@/components/page';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import Prompt from '@/components/Prompt';
import { Tooltip, TooltipContent, TooltipTitle, TooltipTrigger } from '@/components/Tooltip';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import { addMissingDates } from '@/helper/vendorSpendHelper';
import useSessionLock from '@/hooks/useSessionLock';
import { useNotification } from '@/providers/Notification';

import VendorSpendAddVendor from './VendorSpendAddVendor';
import VendorSpendTable from './VendorSpendTable';

// 30 seconds
const REFRESH_SESSION_LOCK_TIME = 30000;

const VendorSpend = () => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const { pushNotification, removeNotification } = useNotification();
  const { data, tagInstallationDate, isLoading, error, mutate } = useVendorSpend(isEditMode);
  const [changedData, setChangedData] = useState<VendorSpendDataType[] | undefined>();
  const [highlightedRowKeys, setHighlightedRowKeys] = useState<string[]>([]);
  const [showAddVendor, setShowAddVendor] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { startSessionLock, endSessionLock } = useSessionLock({
    startSession: startOrContinueSession,
    refreshSession: startOrContinueSession,
    endSession,
    keepAlivePingInterval: REFRESH_SESSION_LOCK_TIME,
  });
  const localData = useMemo(() => changedData ?? data ?? [], [changedData, data]);

  const tableHasData = localData.some((row) => row.data.length > 0);

  // Don't let users create more than 1 manual rows. Imported rows (userSupplied = false) can still
  // have a manual row alongside them.
  const disabledVendorIds = (changedData ?? data ?? [])
    ?.filter((d) => d.data?.[0].userSupplied === true)
    .map((d) => d.vendor.id);

  const totalsData = useMemo(() => {
    return localData.reduce<VendorSpendCellData[]>((acc, row) => {
      if (acc.length) {
        return row.data.map<VendorSpendCellData>((item) => {
          const accItem = acc.find((val) => val.firstDayOfMonth === item.firstDayOfMonth);
          if (accItem) {
            return { ...item, amount: accItem.amount + item.amount };
          } else {
            return { ...item };
          }
        });
      } else {
        return row.data;
      }
    }, []);
  }, [localData]);

  const changeEditMode = async (desiresEditMode: boolean): Promise<boolean> => {
    if (desiresEditMode) {
      const success = await startSessionLock();
      if (success) {
        removeNotification({ type: 'edit' });
        pushNotification({
          type: 'edit',
          message: t`You are currently editing your Vendor Spend, please click Save and Run to process changes`,
        });
        setIsEditMode(true);
      }
      return success;
    } else {
      // we don't care about errors on a lock release, Sol releases locks after 10 mins.
      endSessionLock();
      removeNotification({ type: ['edit', 'error'] });
      setIsEditMode(false);
      setShowAddVendor(false);
      return true;
    }
  };

  const handleBeforeUnload = () => {
    endSessionLock();
  };

  const handleShowAddVendorControls = async () => {
    // if we're already editing, allow it.
    if (isEditMode) {
      setShowAddVendor(true);
    } else {
      const success = await changeEditMode(true);
      const shouldShowAddVendor = !showAddVendor;
      if (shouldShowAddVendor && success) {
        setShowAddVendor(true);
      } else {
        setShowAddVendor(false);
      }
    }
  };

  const handleAddVendorRow = ({ vendor, channel }: { vendor: OptionType; channel: OptionType }) => {
    const rowId = crypto.randomUUID();

    // Use the same range of data as the computed totals, but starting with an amount of $0.
    let cellData = totalsData.map((item) => ({ ...item, amount: 0, userSupplied: true }));

    if (!cellData.length) {
      // We don't have any data from the server. No totals were calculated. This means the user is
      // adding the first row of the table. Fill in the data range starting from tag creation date.
      cellData = addMissingDates(tagInstallationDate);
    }

    setChangedData([
      {
        id: rowId,
        vendor: {
          id: vendor.id,
          name: vendor.name ?? t`Unknown Vendor`,
          channel: {
            id: channel.id,
            name: channel.name ?? t`Unknown Channel`,
          },
        },
        data: cellData,
      },
      ...localData,
    ]);
    setHighlightedRowKeys((prev) => [...prev, rowId]);
    setShowAddVendor(false);
  };

  const handleCancel = () => {
    changeEditMode(false);
    setChangedData(undefined);
  };

  const handleSave = async () => {
    if (changedData != null) {
      setIsSaving(true);

      try {
        const response = await mutateVendorSpend(changedData);
        if (response.success) {
          pushNotification({
            type: 'success',
            message: t`Success! Vendor Spend data has been saved.`,
          });
        } else {
          throw new Error();
        }
      } catch {
        pushNotification({
          type: 'error',
          message: t`Apologies, but something went wrong. Please try to Save again.`,
        });
      }

      setIsSaving(false);
      changeEditMode(false);

      // drop our saved data once we've had a chance to resync with the DB
      await mutate();
      setChangedData(undefined);
    } else {
      changeEditMode(false);
    }
  };

  const handleDataChange = (newData: VendorSpendDataType[]) => {
    setChangedData(newData);
  };

  return (
    <Page
      title={t`Configure`}
      pageName={t`Vendor Spend`}
      exampleAIQuestions={exampleAIQuestions('vendorSpendSettings')}
    >
      {isLoading ? (
        <PageSpinner />
      ) : error ? (
        <PageError
          message={<Trans>An error occurred while loading Vendor Spend</Trans>}
          detail={<Trans>Please refresh the page and try again</Trans>}
          headingLevel={2}
        />
      ) : (
        <div className="flex h-full flex-col gap-6">
          <Prompt
            message={t`Are you sure you want to exit without saving your Vendor Spend changes?`}
            shouldPreventNavigation={changedData != null}
            onBeforeUnload={handleBeforeUnload}
          />
          <div className="flex justify-between">
            <Button variant="secondary" disabled={isSaving} onClick={handleShowAddVendorControls}>
              <PlusIcon />
              <Trans>Vendor</Trans>
            </Button>
            <div className="flex items-center gap-4">
              <PendoPlaceholder />
              {tableHasData && !isEditMode && (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="secondary"
                        shape="square"
                        size="icon"
                        disabled={isSaving}
                        onClick={() => changeEditMode(!isEditMode)}
                      >
                        <EditAddIcon />
                      </Button>
                    }
                  />
                  <TooltipContent side="top" align="end" className="flex-col items-start">
                    <TooltipTitle>
                      <Trans>Add/Edit</Trans>
                    </TooltipTitle>
                    <Trans>Add or Edit Vendor Spend</Trans>
                  </TooltipContent>
                </Tooltip>
              )}
              {isEditMode && (
                <>
                  <Button disabled={isSaving} onClick={handleCancel}>
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    variant="primary"
                    disabled={changedData == null}
                    isLoading={isSaving}
                    onClick={handleSave}
                  >
                    <Trans>Save & Run</Trans>
                  </Button>
                </>
              )}
            </div>
          </div>
          {showAddVendor && (
            <VendorSpendAddVendor
              disabledVendorIds={disabledVendorIds}
              onClose={() => setShowAddVendor(false)}
              onAddVendor={handleAddVendorRow}
            />
          )}
          {data && (
            <VendorSpendTable
              data={localData}
              totalsData={totalsData}
              highlightedRowKeys={highlightedRowKeys}
              editMode={isEditMode}
              onDataChange={handleDataChange}
              onHighlightedRowKeysChange={setHighlightedRowKeys}
            />
          )}
        </div>
      )}
    </Page>
  );
};

export default VendorSpend;
